import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { ROOT_NODE } from '@ws-ui/craftjs-utils';
import {
  addAttributesToContent,
  by,
  EDITOR,
  FileFolderType,
  getDatasourceId,
  IWebFormContent,
  IWebFormStyleClass,
  //removeAttributesString,
  LspActions,
  lspSubject,
  parseAttributes,
  ROLES_TAB_PATH,
  SETTINGS_TAB_PATH,
  toastSubject,
} from '@qodly/shared';
import { get, isEqual, omit, set } from 'lodash';
import { ITab, ITabExtra, ITabFlags, WebformState } from '@qodly/shared';
import {
  getCachedState,
  refactorWebform,
  refactorConditions,
} from '~store/utils';
import { removeDebuggerSessionsNotIn } from '../debugger/thunks';
import {
  deleteFile,
  injectContentAtLine,
  openFileAtLine,
  openFileAtSelection,
  relocateNode,
  removeFileNode,
  renameFile,
} from '../explorer/thunks';
import { saveModel } from '../model';
import {
  updatePrivileges,
  updateRoles,
  updateRolesAndPrivileges,
} from '../roles';
import { setSetting, setSettings } from '../settings';
import { fetchSettings } from '../settings/thunks';
import { TSetSettingPayload } from '../settings/types';
import { setSettingsTabTouchedFlag } from '../settings/utils';
import {
  addDatasource,
  deleteDatasource,
  editDatasource,
  renameDatasource,
} from '../webforms/thunks';
import {
  editDataSource,
  fetchChanges,
  reloadTab,
  renameDataSource,
  saveTab,
  tryInjectMethod,
} from './thunks';
import {
  IRootState,
  TActionAddDataSource,
  TActionAddDefaultWebformState,
  TActionAddWebformState,
  TActionEditWebformState,
  TActionRemoveDataSource,
  TActionRemoveDefaultWebformState,
  TActionRemoveWebformState,
  TActionSetDataSources,
  TActionSetDefaultWebformStates,
  TActionUpdateStateConditionDs,
  TResetComponentState,
} from './types';
import { isBaseState, ROOT_WEBFORM_STATE } from '~store/selectors/states';
import {
  deepDeleteCondition,
  deepSanitizeCondition,
  removeSavedCondition,
  updateDsCondition,
} from '../shared-conditions';

export const stateSlice = createSlice({
  name: 'root',
  initialState: (): IRootState => {
    const cachedState = getCachedState();

    return {
      filesLoading: false,
      tabs: cachedState.tabs || [],
      activeTab: cachedState.activeTab,
      recent: cachedState.recent || [],
      clipboard: null,
    };
  },
  reducers: {
    setWebformState(
      state,
      action: PayloadAction<{ currentState: WebformState; path: string }>,
    ) {
      const { path, currentState } = action.payload;
      state.tabs.forEach((tab) => {
        if (tab.path === path) {
          tab.content.metadata.currentState = currentState.id;
        }
      });
    },
    copyToClipBoard(state, action: PayloadAction<IRootState['clipboard']>) {
      state.clipboard = action.payload;
    },
    setPanelState(
      state,
      action: PayloadAction<Partial<ITab['view']['panel']> & { path: string }>,
    ) {
      const { path, ...payload } = action.payload;
      state.tabs.forEach((tab) => {
        if (tab.path === path) {
          if (!tab.view) {
            // handle missing view.panel in old webforms
            tab.view = {
              panel: {
                isOpen: false,
                type: '',
                current: '',
              },
            };
          }
          Object.assign(tab.view.panel, payload);
        }
      });
    },
    // datasources reducers

    openSettingsTab(state) {
      const tab: ITab = {
        date: new Date().toISOString(),
        flags: { confirmed: true },
        view: {
          panel: {
            isOpen: false,
            type: '',
            current: '',
          },
        },
        extra: {
          editor: {
            key: 'editors:settings',
          },
        },
        name: 'Settings',
        path: SETTINGS_TAB_PATH,
        type: FileFolderType.SETTINGS,
        initialContent: {},
        content: {},
      };

      let found = state.tabs.find((t) => t.path === tab.path);

      if (!found) {
        state.tabs.push(tab);
        found = state.tabs[state.tabs.length - 1];
      }

      if (state.activeTab !== tab.path) {
        const maxTabWeight = state.tabs.reduce<ITab | null>((prev, cur) => {
          if (cur?.weight! > (prev?.weight || 0)) {
            return cur;
          }
          return prev;
        }, null);
        state.activeTab = tab.path;
        found.weight = (maxTabWeight?.weight || 0) + 1;
      }
    },
    addDataSource(state, action: TActionAddDataSource) {
      const {
        payload: { path, datasourceDef },
      } = action;
      const found = state.tabs.find((tab) => tab.path === path);

      if (found) {
        found.content.metadata.datasources =
          found.content.metadata.datasources || [];
        const exists = found.content.metadata.datasources?.find(
          (ds: datasources.ICreateDataSource) => ds.id === datasourceDef.id,
        );
        if (!exists) {
          found.content.metadata.datasources = [
            ...found.content.metadata.datasources,
            { ...datasourceDef, events: [] },
          ];
          found.flags = { ...found.flags, touched: true, confirmed: true };
        }
      }
    },

    addWebformState(state, action: TActionAddWebformState) {
      const {
        payload: { path, stateDef },
      } = action;
      const found = state.tabs.find((tab) => tab.path === path);

      if (found) {
        found.content.metadata.states = found.content.metadata.states || [
          ROOT_WEBFORM_STATE,
        ];

        const exists = found.content.metadata.states?.find(
          (state: WebformState) => state.id === stateDef.id,
        );

        if (!exists) {
          found.content.metadata.states = [
            ...found.content.metadata.states,
            stateDef,
          ];
          found.flags = { ...found.flags, touched: true, confirmed: true };
        }
      }
    },

    removeWebformState(state, action: TActionRemoveWebformState) {
      const { payload } = action;

      state.tabs = state.tabs.map((tab) => {
        if (tab.path === payload.path) {
          return {
            ...tab,
            flags: { ...tab.flags, touched: true, confirmed: true },
            content: refactorWebform(tab.content, payload.state),
          };
        }
        return tab;
      });
    },

    editWebformState(state, action: TActionEditWebformState) {
      const { path, state: webformState, oldLabel } = action.payload;

      state.tabs = state.tabs.map((tab) => {
        if (tab.path === path) {
          return {
            ...tab,
            flags: { ...tab.flags, touched: true, confirmed: true },
            content: {
              ...tab.content,
              metadata: {
                ...tab.content.metadata,
                states: tab.content.metadata.states.map((s: WebformState) => {
                  if (isBaseState(s)) {
                    // the base state should never be altered
                    return s;
                  }

                  if (s.id === webformState.id) {
                    return webformState.conditions
                      ? {
                          ...webformState,
                          conditions: deepSanitizeCondition(
                            webformState.conditions,
                          ),
                        }
                      : webformState;
                  }

                  return {
                    ...s,
                    conditions: s.conditions
                      ? refactorConditions(
                          s.conditions,
                          oldLabel,
                          webformState.label,
                        )
                      : s.conditions,
                  };
                }),
              },
            },
          };
        }
        return tab;
      });
    },

    updateStateConditionDs(state, action: TActionUpdateStateConditionDs) {
      const { path, old_reference, new_reference } = action.payload;

      state.tabs = state.tabs.map((tab) => {
        if (tab.path === path) {
          return {
            ...tab,
            flags: { ...tab.flags, touched: true, confirmed: true },
            content: {
              ...tab.content,
              metadata: {
                ...tab.content.metadata,
                states: (
                  (tab.content?.metadata?.states || []) as WebformState[]
                ).map((state) => {
                  if (state.conditions) {
                    return {
                      ...state,
                      conditions: updateDsCondition(
                        state.conditions,
                        old_reference,
                        new_reference,
                      ),
                    };
                  }
                  return state;
                }),
              },
            },
          };
        }
        return tab;
      });
    },

    resetComponentState(state, action: TResetComponentState) {
      const { path, id, state: webformState } = action.payload;

      state.tabs = state.tabs.map((tab) => {
        if (tab.path === path) {
          return {
            ...tab,
            flags: { ...tab.flags, touched: true, confirmed: true },
            content: {
              ...tab.content,
              components: {
                ...tab.content.components,
                [id]: {
                  ...tab.content.components[id],
                  custom: {
                    ...omit(
                      tab.content.components[id].custom,
                      `states:${webformState.id}`,
                    ),
                  },
                },
              },
            },
          };
        }
        return tab;
      });
    },

    setDefaultWebformStates(state, action: TActionSetDefaultWebformStates) {
      const { path, states } = action.payload;

      state.tabs = state.tabs.map((tab) => {
        if (tab.path === path) {
          return {
            ...tab,
            flags: { ...tab.flags, touched: true, confirmed: true },
            content: {
              ...tab.content,
              metadata: {
                ...tab.content.metadata,
                defaultStates: states.map(({ id }) => id),
              },
            },
          };
        }
        return tab;
      });
    },

    addDefaultWebformState(state, action: TActionAddDefaultWebformState) {
      const { payload } = action;

      state.tabs = state.tabs.map((tab) => {
        if (tab.path === payload.path) {
          return {
            ...tab,
            flags: { ...tab.flags, touched: true, confirmed: true },
            content: {
              ...tab.content,
              metadata: {
                ...tab.content.metadata,
                defaultStates: Array.from(
                  new Set([
                    ...(tab.content.metadata?.defaultStates || []),
                    payload.state.id,
                  ]),
                ),
              },
            },
          };
        }
        return tab;
      });
    },

    removeDefaultWebformState(state, action: TActionRemoveDefaultWebformState) {
      const { payload } = action;

      state.tabs = state.tabs.map((tab) => {
        if (tab.path === payload.path) {
          return {
            ...tab,
            flags: { ...tab.flags, touched: true, confirmed: true },
            content: {
              ...tab.content,
              metadata: {
                ...tab.content.metadata,
                defaultStates: (
                  tab.content.metadata?.defaultStates || []
                ).filter((id: string) => id !== payload.state.id),
              },
            },
          };
        }
        return tab;
      });
    },

    removeDataSource(state, action: TActionRemoveDataSource) {
      const { path, datasourceID } = action.payload;

      state.tabs = state.tabs.map((tab) => {
        if (tab.path === path) {
          return {
            ...tab,
            flags: { ...tab.flags, touched: true, confirmed: true },
            content: {
              ...tab.content,
              metadata: {
                ...tab.content.metadata,
                datasources: tab.content.metadata.datasources.filter(
                  (ds: datasources.ICreateDataSource) => ds.id !== datasourceID,
                ),
              },
            },
          };
        }
        return tab;
      });
    },

    setDataSources(state, action: TActionSetDataSources) {
      const {
        payload: { path, datasourceArr },
      } = action;

      state.tabs = state.tabs.map((tab) => {
        if (tab.path === path) {
          return {
            ...tab,
            flags: { ...tab.flags, touched: true, confirmed: true },
            content: {
              ...tab.content,
              metadata: {
                ...tab.content?.metadata,
                datasources: datasourceArr,
              },
            },
          };
        }
        return tab;
      });
    },
    // tabs reducers
    setTabs(state, action: PayloadAction<ITab[]>) {
      state.tabs = action.payload;
    },
    closeTab(state, action: PayloadAction<number>) {
      const index = action.payload;
      state.tabs.splice(index, 1);
      const maxTabWeight = state.tabs.reduce<ITab | null>((prev, cur) => {
        if (cur?.weight! > (prev?.weight || 0)) {
          return cur;
        }
        return prev;
      }, null);
      state.activeTab = maxTabWeight?.path || '';
    },
    closeOthers(state, action: PayloadAction<string>) {
      state.tabs = state.tabs.filter((t) => t.path === action.payload);
      state.activeTab = action.payload;
    },
    closeAllTabs(state) {
      state.tabs = [];
      state.activeTab = '';
    },
    closeRightTabs(state, action: PayloadAction<number>) {
      state.tabs.splice(action.payload + 1);
      state.activeTab = state.tabs[action.payload].path;
    },
    closeLeftTabs(state, action: PayloadAction<number>) {
      state.tabs.splice(0, action.payload);
    },
    closeSavedTabs(state) {
      state.tabs = state.tabs.filter(({ flags: { touched } }) => touched);
      const maxTabWeight = state.tabs.reduce<ITab | null>((prev, cur) => {
        if (cur?.weight! > (prev?.weight || 0)) {
          return cur;
        }
        return prev;
      }, null);
      state.activeTab = maxTabWeight?.path || '';
    },
    confirmTab(state, action: PayloadAction<string>) {
      // Find the tab object from the path
      const tab = state.tabs.find(({ path }) => path === action.payload);

      if (!tab) {
        return;
      }

      // Return if we try to confirm the already confirmed tab
      if (tab.flags.confirmed) {
        return;
      }

      // set the confirmed flag to true using "setTabFlags"
      stateSlice.caseReducers.setTabFlags(state, {
        type: action.type,
        payload: { path: action.payload, flags: { confirmed: true } },
      });
    },
    selectTab(state, action: PayloadAction<string>) {
      // Find the tab object from the path
      const tab = state.tabs.find(({ path }) => path === action.payload);

      if (!tab) {
        return;
      }

      // Return if we try to select the already selected tab
      if (state.activeTab === action.payload) {
        return;
      }

      const tabWithMaxWeight = state.tabs.reduce<ITab | null>((prev, cur) => {
        if (cur?.weight! > (prev?.weight || 0)) {
          return cur;
        }
        return prev;
      }, null);
      tab.weight = tabWithMaxWeight ? (tabWithMaxWeight.weight || 0) + 1 : 1;
      state.activeTab = action.payload;
    },
    openTabUnselected(state, action: PayloadAction<ITab>) {
      const tab = action.payload;
      let found = state.tabs.find(({ path }) => tab.path === path);

      if (!found) {
        state.tabs.push(tab);
        found = state.tabs[state.tabs.length - 1];
      }

      const tabWithMaxWeight = state.tabs.reduce<ITab | null>((prev, cur) => {
        if (cur?.weight! > (prev?.weight || 0)) {
          return cur;
        }
        return prev;
      }, null);
      found.weight = tabWithMaxWeight ? (tabWithMaxWeight.weight || 0) + 1 : 1;

      found.extra = {
        ...found.extra,
        ...tab.extra,
      };
    },
    selectTabByReference(
      state,
      action: PayloadAction<ITab & { isNew?: boolean }>,
    ) {
      const confirmedTabsType = [FileFolderType.DEBUGGER];
      const { isNew, ...rest } = action.payload;
      let tab = rest;
      let found = state.tabs.find(({ path }) => tab.path === path);

      if (!tab.history) {
        tab.history = {
          stack: [],
          active: -1,
          locked: false,
        };
      }

      if (confirmedTabsType.includes(tab.type) || found?.flags.confirmed) {
        tab = {
          ...tab,
          flags: {
            ...tab.flags,
            confirmed: true,
          },
        };
      }

      if (!found) {
        const foundWithPreview = state.tabs.findIndex(
          ({ flags: { confirmed } }) => confirmed === false,
        );
        if (
          foundWithPreview !== -1 &&
          !confirmedTabsType.includes(tab.type) &&
          !isNew
        ) {
          if (state.activeTab === state.tabs[foundWithPreview].path) {
            state.tabs[foundWithPreview] = tab;
            found = state.tabs[foundWithPreview];
          } else {
            state.tabs.push(tab);
            found = state.tabs[state.tabs.length - 1];
            state.tabs.splice(foundWithPreview, 1);
          }
        } else {
          state.tabs.push(tab);
          found = state.tabs[state.tabs.length - 1];
        }
      }

      if (state.activeTab !== tab.path) {
        const maxTabWeight = state.tabs.reduce<ITab | null>((prev, cur) => {
          if (cur?.weight! > (prev?.weight || 0)) {
            return cur;
          }
          return prev;
        }, null);
        state.activeTab = tab.path;
        found!.weight = (maxTabWeight?.weight || 0) + 1;
      }

      found.extra = {
        ...found.extra,
        ...tab.extra,
      };

      found.flags = {
        ...(found.flags || {}),
        ...(tab.flags || {}),
      };
    },
    setContent(
      state,
      action: PayloadAction<{
        path: string;
        content: any;
        contentPath?: string;
      }>,
    ) {
      const { path, content, contentPath } = action.payload;
      state.tabs = state.tabs.map((tab) => {
        if (tab.path !== path) {
          return tab;
        }

        let touched = false;
        let confirmed = tab.flags.confirmed;

        if (typeof content === 'string') {
          touched = tab.initialContent !== content;
          confirmed = !confirmed && touched ? true : confirmed;
        } else {
          const oldContent = get(
            tab.initialContent,
            contentPath || '',
            tab.initialContent,
          );

          const newContent = get(content, contentPath || '', content);

          touched = !isEqual(oldContent, newContent);
          confirmed = !confirmed && touched ? true : confirmed;
        }

        if (content && typeof content === 'object') {
          /**
           * So, basically, we spent 4 hours trying to debug a weird behaviour
           * Where everytime you select component, the style array gets wiped out
           * To remedy the problem, we decided to use the handy "set" method from lodash
           * Documentation: https://lodash.com/docs/4.17.15#set
           * @signature Ibrahim & Mourad (sleepy) & Simo
           * @at 01:10
           */
          if (contentPath) {
            set(tab.content, contentPath, content);
          } else {
            tab.content = content;
          }
        }
        if (typeof content === 'string') {
          tab.content = content;
        }
        return {
          ...tab,
          flags: {
            ...tab.flags,
            touched,
            confirmed,
          },
        };
      });
    },
    setTabFlags(
      state,
      action: PayloadAction<{
        path: string;
        flags: ITabFlags;
      }>,
    ) {
      const { path, flags } = action.payload;
      state.tabs = state.tabs.map((tab) =>
        tab.path === path
          ? {
              ...tab,
              flags: {
                ...tab.flags,
                ...flags,
              },
            }
          : tab,
      );
    },
    setTabsFlags(
      state,
      action: PayloadAction<{
        tabs: string[];
        flags: ITabFlags;
      }>,
    ) {
      const { tabs, flags } = action.payload;
      state.tabs = state.tabs.map((tab) =>
        tabs.includes(tab.path)
          ? {
              ...tab,
              flags: {
                ...tab.flags,
                ...flags,
              },
            }
          : tab,
      );
    },
    setTabExtra(
      state,
      action: PayloadAction<{
        path: string;
        extra: ITabExtra;
        flags?: ITabFlags;
      }>,
    ) {
      const { path, extra, flags } = action.payload;
      state.tabs = state.tabs.map((tab) =>
        tab.path === path
          ? {
              ...tab,
              extra: {
                ...tab.extra,
                ...extra,
              },
              flags: flags
                ? {
                    ...tab.flags,
                    ...flags,
                  }
                : tab.flags,
            }
          : tab,
      );
    },
    addWebFormStyles(
      state,
      action: PayloadAction<{
        path: string;
        styles: IWebFormStyleClass[];
      }>,
    ) {
      const { path, styles = [] } = action.payload;
      const found = state.tabs.find((tab) => tab.path === path);
      if (found) {
        const currentClassNames = found.content.metadata.styles.map(
          ({ name }: IWebFormStyleClass) => name,
        );
        const newStyles = styles.filter(
          ({ name }) => !currentClassNames.includes(name),
        );
        found.content.metadata.styles = [
          ...(found.content.metadata.styles as IWebFormStyleClass[]),
          ...newStyles,
        ].map(({ scope, ...cssClass }) => ({
          ...cssClass,
        }));
      }
    },
    changeTabEditor(state, action: PayloadAction<ITab>) {
      state.tabs = state.tabs.map((tab) => {
        if (tab.path === action.payload.path) {
          return action.payload;
        }

        return tab;
      });

      return state;
    },
    setHistoryContent(state, action: PayloadAction<string>) {
      const path = action.payload;

      state.tabs = state.tabs.map((tab) => {
        if (tab.path !== path) {
          return tab;
        }

        const isChanged = !isEqual(
          tab.content,
          tab.history?.stack[tab.history.active],
        );

        if (tab.history && isChanged) {
          tab.history.stack = [
            ...tab.history?.stack.slice(0, tab.history.active + 1),
            tab.content,
          ];
          tab.history.active = tab.history.stack.length - 1;
        }
        return {
          ...tab,
        };
      });
    },
    unlockHistory(state, action: PayloadAction<string>) {
      const path = action.payload;

      state.tabs = state.tabs.map((tab) => {
        if (tab.path !== path) {
          return tab;
        }

        if (tab.history) {
          tab.history.locked = false;
        }

        return {
          ...tab,
        };
      });
    },
    undo(state) {
      state.tabs = state.tabs.map((tab) => {
        if (tab.path !== state.activeTab) {
          return tab;
        }

        if (tab.history && tab.history.active > 0) {
          tab.history.locked = true;
          tab.history.active--;
          tab.content = tab.history.stack[tab.history.active];
        }

        return {
          ...tab,
        };
      });
    },
    redo(state) {
      state.tabs = state.tabs.map((tab) => {
        if (tab.path !== state.activeTab) {
          return tab;
        }

        if (tab.history && tab.history.active < tab.history.stack.length - 1) {
          tab.history.locked = true;
          tab.history.active++;
          tab.content = tab.history.stack[tab.history.active];
        }

        return {
          ...tab,
        };
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setSetting, (state, action) => {
      if (
        action.payload.settingsKey === 'studio.enablePreview' &&
        action.payload.value === false
      ) {
        const foundWithPreview = state.tabs.find(
          ({ flags: { confirmed } }) => confirmed === false,
        );

        if (foundWithPreview) {
          stateSlice.caseReducers.setTabFlags(state, {
            type: '',
            payload: {
              path: foundWithPreview.path,
              flags: { confirmed: true },
            },
          });
        }
      }
    });
    builder.addCase(injectContentAtLine.fulfilled, (state, action) => {
      if (!action.payload) return;
      const { content, line, path } = action.payload;

      const tab = state.tabs.find((tab) => tab.path === path);

      if (!tab) return;

      if (typeof tab.content === 'string') {
        const lines = tab.content.trim().split('\n');
        lines.splice(line, 0, content);

        tab.content = lines.join('\n');
      }
    });
    // builder.addCase(fetchAppSettings.fulfilled, (state, action) => {
    //   state.settings.app = action.payload;
    // });
    builder.addCase(saveTab.pending, (_state, action) => {
      const { tab } = action.meta.arg;

      switch (tab.type) {
        case FileFolderType.CLASS:
        case FileFolderType.METHOD:
          lspSubject.next({
            action: LspActions.WILL_SAVE,
            payload: {
              filePath: tab.path,
            },
          });
          break;
      }
    });
    builder.addCase(saveTab.fulfilled, (state, action) => {
      if (!action.payload) return;

      const found = state.tabs.find(
        ({ path }) => path === action.payload?.path,
      );

      if (!found) {
        return;
      }
      found.content = action.payload.content;
      found.date = action.payload.date;
      found.initialContent = action.payload.initialContent;
      found.flags.touched = false;
      found.flags.confirmed = true;
      found.flags.old = false;

      switch (found.type) {
        case FileFolderType.CLASS:
        case FileFolderType.METHOD:
          lspSubject.next({
            action: LspActions.DID_SAVE,
            payload: {
              filePath: found.path,
              content: found.content,
            },
          });
          break;
      }
      if (action.payload.errors.length > 0) {
        toastSubject.next({
          kind: 'error',
          message: action.payload.errors,
        });
      }
    });

    builder.addCase(saveTab.rejected, () => {
      toastSubject.next({
        kind: 'danger',
        message: 'Cannot save file',
      });
    });

    // Fetch Changes
    builder.addCase(fetchChanges.fulfilled, (state, action) => {
      const filesInfo = [...action.payload.files];

      state.tabs = state.tabs.map((tab) => {
        if ([FileFolderType.CLASS, FileFolderType.METHOD].includes(tab.type)) {
          const newInfo = filesInfo.find(
            (f) => f.name === tab.name && f.type === tab.type,
          );
          if (newInfo) {
            const currentModifiedDate = new Date(tab.date);
            const fetchedModifiedDate = new Date(newInfo.date);

            if (
              currentModifiedDate.getTime() !== fetchedModifiedDate.getTime()
            ) {
              return {
                ...tab,
                flags: {
                  ...tab.flags,
                  old: true,
                },
              };
            }
          }
        }
        return tab;
      });
    });

    builder.addCase(reloadTab.pending, (state, action) => {
      state.tabs = state.tabs.map((tab) => {
        if (tab.path === action.meta.arg.path) {
          return {
            ...tab,
            flags: {
              ...tab.flags,
              loading: true,
            },
          };
        }
        return tab;
      });
    });

    builder.addCase(reloadTab.fulfilled, (state, action) => {
      const newFile = action.payload;
      if (newFile) {
        const target = action.meta.arg;
        state.tabs = state.tabs.map((tab) => {
          if (tab.name === target.name && tab.type === target.type) {
            const extra = tab.extra || {};
            if (tab.type === FileFolderType.METHOD) {
              extra.attributes = parseAttributes(newFile.content);
              //newFile.content = removeAttributesString(newFile.content);
              newFile.content = addAttributesToContent(newFile.content, {});
            } else if (tab.type === FileFolderType.SETTINGS) {
              // do not save settings file content in the tab state
              newFile.content = {};
            }

            return {
              ...tab,
              initialContent: newFile.content,
              content: newFile.content,
              date: newFile.date,
              flags: {
                enabled: tab.flags.enabled,
                confirmed: tab.flags.confirmed,
              },
              extra,
            };
          }
          return tab;
        });
      }
    });

    builder.addCase(reloadTab.rejected, (state, action) => {
      const currentTabPath = action.meta.arg.path;
      state.tabs = state.tabs.map((tab) => {
        if (tab.path === currentTabPath) {
          return {
            ...tab,
            flags: {
              ...tab.flags,
              loading: false,
            },
          };
        }
        return tab;
      });
    });

    // explorer actions effects
    builder.addCase(openFileAtLine.fulfilled, (state, action) => {
      if (!action.payload) return;

      const { line, offset } = action.meta.arg;
      const found = state.tabs.find(
        ({ path }) => path === action.payload?.path,
      );

      if (found) {
        found.extra = {
          ...found.extra,
          initialLineInfo: {
            line,
            offset,
            lastModification: Date.now(),
          },
        };
      }
    });

    builder.addCase(openFileAtSelection.fulfilled, (state, action) => {
      if (!action.payload) return;

      const { selection } = action.meta.arg;
      const found = state.tabs.find(
        ({ path }) => path === action.payload?.path,
      );

      if (found) {
        found.extra = {
          ...found.extra,
          initialSelectionInfo: {
            selection,
            lastModification: Date.now(),
          },
        };
      }
    });

    builder.addCase(renameFile.fulfilled, (state, action) => {
      if (action.payload.new !== action.payload.old) {
        state.tabs = [...state.tabs].map((tab) => {
          if (tab.path === action.payload.old.path) {
            return {
              ...tab,
              path: action.payload.new.path,
              name: action.payload.new.name,
            };
          }
          return tab;
        });

        if (state.activeTab === action.payload.old.path) {
          state.activeTab = action.payload.new.path;
        }
      }
    });

    builder.addCase(deleteFile.pending, (_state, action) => {
      const file = action.meta.arg;
      const filePath = `${file.path}${file.ext ? `.${file.ext}` : ''}`;
      switch (file.ext) {
        case '4dm':
        case '4qs':
          lspSubject.next({
            action: LspActions.WILL_DELETE,
            payload: {
              filePath,
            },
          });
          break;
      }
    });

    builder.addCase(deleteFile.fulfilled, (state, action) => {
      const { deleted, node } = action.payload;
      if (!deleted) {
        return;
      }

      const filePath = `${node.path}${node.ext ? `.${node.ext}` : ''}`;
      switch (node.ext) {
        case '4dm':
        case '4qs':
          lspSubject.next({
            action: LspActions.DID_DELETE,
            payload: {
              filePath,
            },
          });
          break;
      }

      state.tabs = state.tabs.filter((tab) => {
        return tab.path !== filePath;
      });

      state.recent = state.recent.filter(({ path }) => filePath !== path);

      if (state.tabs.length) {
        state.activeTab = state.tabs[state.tabs.length - 1].path;

        return;
      }

      state.activeTab = '';
    });

    builder.addCase(removeFileNode.pending, (_state, action) => {
      const file = action.meta.arg;
      const filePath = `${file.path}${file.ext ? `.${file.ext}` : ''}`;
      switch (file.ext) {
        case '4dm':
        case '4qs':
          lspSubject.next({
            action: LspActions.WILL_DELETE,
            payload: {
              filePath,
            },
          });
          break;
      }
    });

    builder.addCase(removeFileNode.fulfilled, (_state, action) => {
      const { node } = action.payload;
      const filePath = `${node.path}${node.ext ? `.${node.ext}` : ''}`;
      switch (node.ext) {
        case '4dm':
        case '4qs':
          lspSubject.next({
            action: LspActions.DID_DELETE,
            payload: {
              filePath,
            },
          });
          break;
      }
    });

    builder.addCase(relocateNode.fulfilled, (state, action) => {
      const { draggedNode, newLocation } = action.meta.arg;

      if (
        !state.tabs.find(
          (node) =>
            node.path ===
            `${draggedNode.path}${
              draggedNode.ext ? `.${draggedNode.ext}` : ''
            }`,
        )
      )
        return;

      state.tabs = state.tabs.map((tab) => {
        if (
          tab.path ===
          `${draggedNode.path}${draggedNode.ext ? `.${draggedNode.ext}` : ''}`
        ) {
          return {
            ...tab,
            path: `${newLocation.path}/${draggedNode.name}.${draggedNode.ext}`,
          };
        }

        return tab;
      });

      state.recent = state.recent.map((tab) => {
        if (
          tab.path ===
          `${draggedNode.path}${draggedNode.ext ? `.${draggedNode.ext}` : ''}`
        ) {
          return {
            ...tab,
            path: `${newLocation.path}/${draggedNode.name}.${draggedNode.ext}`,
          };
        }

        return tab;
      });

      state.activeTab = `${newLocation.path}/${draggedNode.name}.${draggedNode.ext}`;

      return state;
    });

    builder.addCase(saveModel.fulfilled, (state, action) => {
      const found = state.tabs.find((tab) => tab.path === action.payload.path);

      if (found) {
        found.flags.touched = false;
        found.flags.confirmed = true;
        found.date = action.payload.date;
        found.content = action.payload.content;
        found.initialContent = action.payload.content;
      }
    });

    builder.addCase(tryInjectMethod.fulfilled, (state, action) => {
      if (!action.payload?.path) return;

      const tab = state.tabs.find(by('path', action.payload.path));

      if (!tab) return;
      tab.content =
        tab.content + `\nexposed Function ${action.meta.arg.name}()\n`;
      tab.flags = { ...tab.flags, touched: true, confirmed: true };
      const lineCount = tab.content.split('\n');

      if (tab.path.startsWith('Classes/')) {
        tab.type = FileFolderType.CLASS;
      }

      tab.extra = {
        ...tab.extra,
        editor: EDITOR.Code,
        initialLineInfo: {
          line: lineCount.length - 1,
          lastModification: Date.now(),
        },
      };
    });

    builder.addCase(renameDataSource.fulfilled, (state, action) => {
      // if local datasource -> update the name
      if (!action.payload?.path || action.payload?.namespace) return;

      const currWebform = action.payload.path;

      const currentTab = state.tabs.find(
        by('path', currWebform),
      ) as ITab<IWebFormContent>;

      if (!currentTab || !currentTab.content || !currentTab.content.metadata)
        return;
      Object.assign(currentTab.content.metadata, {
        datasources: action.payload.datasources,
      });
      currentTab.flags.touched = true;
      currentTab.flags.confirmed = true;
    });

    builder.addCase(editDataSource.fulfilled, (state, action) => {
      const {
        payload: ds_array,
        meta: { arg: payload },
      } = action;

      if (payload.namespace) return;

      state.tabs.forEach((tab) => {
        if (tab.path === payload.path)
          Object.assign(tab.content as IWebFormContent, {
            metadata: { ...tab.content.metadata, datasources: ds_array },
          });
      });
    });

    builder.addCase(removeDebuggerSessionsNotIn.fulfilled, (state, action) => {
      const { staleThreadIds } = action.payload;
      if (staleThreadIds.length === 0) {
        return;
      }

      const tabsToRemove = staleThreadIds.map(
        (threadId) => `Debugger/${threadId}`,
      );

      state.tabs = state.tabs.filter((tab) => {
        return !tabsToRemove.includes(tab.path);
      });

      state.recent = state.recent.filter(
        ({ path }) => !tabsToRemove.includes(path),
      );

      if (state.tabs.length) {
        state.activeTab = state.tabs[state.tabs.length - 1].path;
        return;
      }

      state.activeTab = '';
    });

    builder.addCase(fetchSettings.pending, (state) =>
      setSettingsTabTouchedFlag(state, false),
    );

    builder.addCase(removeSavedCondition, (state, action) => {
      const { id } = action.payload;
      state.tabs = state.tabs.map((tab) => {
        return {
          ...tab,
          content: {
            ...tab.content,
            metadata: {
              ...tab.content.metadata,
              states: (tab.content.metadata?.states || []).map(
                (state: WebformState) => {
                  if (state.conditions) {
                    const conditions = deepDeleteCondition(
                      state.conditions,
                      id,
                    );
                    return conditions
                      ? {
                          ...state,
                          conditions,
                        }
                      : omit(state, 'conditions');
                  }

                  return state;
                },
              ),
            },
          },
        };
      });
    });

    builder.addMatcher(
      isAnyOf(setSetting),
      (state, action: TSetSettingPayload) => {
        if (['project'].includes(action.payload.path)) {
          setSettingsTabTouchedFlag(state, true);
        }
      },
    );

    builder.addMatcher(isAnyOf(setSettings), (state) => {
      setSettingsTabTouchedFlag(state, false);
    });

    builder.addMatcher(
      isAnyOf(selectTab, selectTabByReference),
      (state, action: PayloadAction<string> | PayloadAction<ITab>) => {
        const { payload } = action;
        let found: ITab | undefined;

        if (typeof payload === 'string') {
          found = state.tabs.find(({ path }) => payload === path);
        } else {
          found = payload;
        }

        if (found) {
          if (state.tabs.length > 10) {
            const idleTabs = state.tabs
              .toSorted((a, b) => (b.weight || 0) - (a.weight || 0))
              .slice(10)
              .map((tab) => tab.path);
            state.tabs = state.tabs.map((tab) => {
              if (tab.path === found.path) {
                return { ...tab, flags: { ...tab.flags, inactive: false } };
              } else if (idleTabs.includes(tab.path)) {
                return { ...tab, flags: { ...tab.flags, inactive: true } };
              }
              return tab;
            });
          }
          state.recent = [
            omit(found, 'content', 'initialContent', 'history'),
            ...state.recent.filter(
              ({ path }) => ![found!.path, 'settings'].includes(path),
            ),
          ];
        }
      },
    );

    builder.addMatcher(isAnyOf(updateRoles, updatePrivileges), (state) => {
      state.tabs = state.tabs.map((tab) => {
        if (tab.path === ROLES_TAB_PATH) {
          return {
            ...tab,
            flags: {
              ...tab.flags,
              confirmed: true,
              touched: true,
            },
          };
        }

        return tab;
      });
    });

    builder.addMatcher(isAnyOf(updateRolesAndPrivileges), (state) => {
      state.tabs = state.tabs.map((tab) => {
        if (tab.path === ROLES_TAB_PATH) {
          return {
            ...tab,
            flags: {
              ...tab.flags,
              touched: false,
            },
          };
        }

        return tab;
      });
    });

    builder.addMatcher(
      isAnyOf(
        addDatasource.fulfilled,
        editDatasource.fulfilled,
        deleteDatasource.fulfilled,
        renameDatasource.fulfilled,
      ),
      (state, action) => {
        const tab = state.tabs.find(
          by('path', action.payload?.path || action.meta.arg.path),
        );
        if (!tab || action.payload?.namespace) return;

        tab.flags.touched = true;
        tab.flags.confirmed = true;
        Object.assign(tab.content, {
          metadata: {
            ...tab.content.metadata,
            datasources: action.payload?.list,
          },
        });
      },
    );

    builder.addMatcher(
      isAnyOf(deleteDatasource.fulfilled, renameDatasource.fulfilled),
      (state, action) => {
        const tab = state.tabs.find(
          by('path', action.payload?.path || action.meta.arg.path),
        );
        if (!tab) return;
        const { panel } = tab.view;
        const incomingDsId = getDatasourceId({
          id: action.meta.arg.id,
          namespace: action.meta.arg.namespace,
        });
        const isCurrent =
          panel.type === 'datasource' && panel.current === incomingDsId;

        if (!isCurrent) return;

        switch (action.type) {
          case deleteDatasource.fulfilled.type:
            Object.assign(tab.view.panel, {
              type: 'component',
              current: ROOT_NODE,
            });
            break;
          case renameDatasource.fulfilled.type:
            const newId = getDatasourceId({
              id: (action.meta.arg as any).new_name,
              namespace: action.meta.arg.namespace,
            });
            Object.assign(tab.view.panel, {
              type: 'datasource',
              current: newId,
            });
            break;
        }
      },
    );
  },
});

export const {
  copyToClipBoard,
  setTabs,
  closeTab,
  setContent,
  setTabFlags,
  setTabsFlags,
  setTabExtra,
  closeAllTabs,
  addDataSource,
  closeRightTabs,
  closeOthers,
  closeLeftTabs,
  closeSavedTabs,
  setDataSources,
  removeDataSource,
  confirmTab,
  selectTab,
  selectTabByReference,
  openTabUnselected,
  addWebFormStyles,
  changeTabEditor,
  openSettingsTab,
  setWebformState,
  setPanelState,
  addWebformState,
  editWebformState,
  updateStateConditionDs,
  removeWebformState,
  resetComponentState,
  addDefaultWebformState,
  removeDefaultWebformState,
  setDefaultWebformStates,
  setHistoryContent,
  unlockHistory,
  undo,
  redo,
} = stateSlice.actions;
export default stateSlice;
